import type { ApiClient } from '@momonos/api-client'
import { formatBdt } from '@momonos/shared'
import { Card, Loader } from '@momonos/ui'
import { useEffect, useState } from 'react'

/** FR-ADM-2 — the cards a manager checks each morning. */
interface DashboardData {
  businessDate: string
  cards: {
    orders: number
    revenue: number
    pendingPayment: number
    cancelled: number
    averageBasket: number
    avgPrepSeconds: number | null
    currentWaitSeconds: number
    kitchenQueueDepth: number
    lowStockCount: number
  }
  topSelling: Array<{ productId: string; name: string; quantity: number; revenue: number }>
  paymentSplit: Array<{ method: string; amount: number; count: number }>
  lowStock: Array<{ ingredientId: string; name: string; stock: number; unit: string }>
}

export function DashboardScreen({ api, branchId }: { api: ApiClient; branchId: string | null }) {
  const [data, setData] = useState<DashboardData | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const result = await api.get<DashboardData>('/v1/admin/dashboard', {
          query: { branch: branchId ?? undefined },
        })
        if (!cancelled) {
          setData(result)
          setError(false)
        }
      } catch {
        if (!cancelled) setError(true)
      }
    }

    void load()
    // FR-ANA-3 — today's numbers refresh on their own.
    const interval = setInterval(load, 60_000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [api, branchId])

  if (error) return <p className="text-momo-red">Could not load the dashboard.</p>
  if (!data) return <Loader label="Loading the dashboard" />

  const minutes = (seconds: number | null) =>
    seconds == null ? '—' : `${Math.round(seconds / 60)} min`

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-bold text-2xl text-content">Today</h1>
        <p className="text-content-muted text-sm">{data.businessDate}</p>
      </header>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Orders" value={String(data.cards.orders)} />
        <Stat label="Revenue" value={formatBdt(data.cards.revenue)} />
        <Stat label="Average basket" value={formatBdt(data.cards.averageBasket)} />
        <Stat label="Awaiting payment" value={String(data.cards.pendingPayment)} />
        <Stat label="Kitchen queue" value={String(data.cards.kitchenQueueDepth)} />
        <Stat label="Waiting now" value={minutes(data.cards.currentWaitSeconds)} />
        <Stat label="Kitchen speed" value={minutes(data.cards.avgPrepSeconds)} />
        <Stat
          label="Low stock"
          value={String(data.cards.lowStockCount)}
          tone={data.cards.lowStockCount > 0 ? 'warn' : 'normal'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <h2 className="mb-3 font-semibold text-content">Top selling</h2>
          {data.topSelling.length === 0 ? (
            <p className="text-content-muted text-sm">Nothing sold yet today.</p>
          ) : (
            <ul className="space-y-2">
              {data.topSelling.map((item) => (
                <li key={item.productId} className="flex justify-between text-content-secondary">
                  <span>{item.name}</span>
                  <span className="tabular">
                    {item.quantity} · {formatBdt(item.revenue)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <h2 className="mb-3 font-semibold text-content">Payments</h2>
          {data.paymentSplit.length === 0 ? (
            <p className="text-content-muted text-sm">No payments yet today.</p>
          ) : (
            <ul className="space-y-2">
              {data.paymentSplit.map((row) => (
                <li key={row.method} className="flex justify-between text-content-secondary">
                  <span className="capitalize">{row.method.replace('_manual', '')}</span>
                  <span className="tabular">{formatBdt(row.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {data.lowStock.length > 0 && (
        <Card className="border-momo-yellow/40">
          <h2 className="mb-3 font-semibold text-momo-yellow">Running low</h2>
          <ul className="space-y-2">
            {data.lowStock.map((row) => (
              <li key={row.ingredientId} className="flex justify-between text-content-secondary">
                <span>{row.name}</span>
                <span className="tabular">
                  {row.stock} {row.unit}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  tone = 'normal',
}: {
  label: string
  value: string
  tone?: 'normal' | 'warn'
}) {
  return (
    <Card>
      <p className="text-content-muted text-sm">{label}</p>
      <p
        className={
          tone === 'warn'
            ? 'mt-1 font-bold text-2xl text-momo-yellow tabular'
            : 'mt-1 font-bold text-2xl text-content tabular'
        }
      >
        {value}
      </p>
    </Card>
  )
}
